// src/components/condos/CondosPage.tsx
// User-facing page to browse condos (Discover Condos)

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCondos } from '../../hooks/useCondos';
import { Condo } from '../../types/condo';
import { DiscoverFilters as DiscoverFiltersType } from '../../types/discover';
import BackgroundShapes from '../common/BackgroundShapes';
import CondoCard from './CondoCard';
import WaitlistModal from './WaitlistModal';
import DiscoverFilters from '../discover/DiscoverFilters';

const CondosPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const {
    condos,
    pagination,
    loading,
    error,
    fetchCondos,
    goToPage,
    getWaitlistUsers,
    joinWaitlist,
    leaveWaitlist,
    setCondoIsOnWaitlist,
  } = useCondos();

  const [locationFilters, setLocationFilters] = useState<DiscoverFiltersType>({});
  const [showFilters, setShowFilters] = useState(false);

  const [waitlistModal, setWaitlistModal] = useState<{
    isOpen: boolean;
    condo: Condo | null;
  }>({ isOpen: false, condo: null });

  useEffect(() => {
    fetchCondos(1, 20);
  }, [fetchCondos]);

  const handleApplyFilters = useCallback((filters: DiscoverFiltersType) => {
    setLocationFilters(filters);
    fetchCondos(1, 20, {
      locations: filters.locations,
      neighborhoodIds: filters.neighborhoodIds,
    });
  }, [fetchCondos]);

  const handleClearFilters = useCallback(() => {
    setLocationFilters({});
    fetchCondos(1, 20);
  }, [fetchCondos]);

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleCheckWaitlist = (condoId: string) => {
    const condo = condos.find((c) => (c.id ?? c._id) === condoId) ?? null;
    setWaitlistModal({ isOpen: true, condo });
  };

  const handleWaitlistClose = () => {
    setWaitlistModal({ isOpen: false, condo: null });
  };

  const totalPages = pagination.limit > 0
    ? Math.ceil(pagination.total / pagination.limit)
    : 0;

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            {isAuthenticated && (
              <button
                onClick={handleBackToDashboard}
                className="bg-cream border-2 border-coffee text-coffee px-3 py-2 rounded-lg hover:bg-white transition-colors shadow-sm text-sm font-medium"
              >
                ← Dashboard
              </button>
            )}
            <div>
              <h1 className="text-3xl font-bold text-coffee mb-1">Discover Condos</h1>
              <p className="text-coffee opacity-80 text-sm">
                Browse condos and find roommates for specific buildings
              </p>
            </div>
          </div>
          {isAuthenticated && (
            <div className="flex space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors text-sm font-medium"
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </button>
              <button
                onClick={handleClearFilters}
                className="bg-white border-2 border-terracotta text-terracotta px-4 py-2 rounded-lg hover:bg-terracotta hover:text-white transition-colors text-sm font-medium"
              >
                Clear All
              </button>
              <button
                onClick={() => navigate('/condos/create')}
                className="bg-cream border-2 border-coffee text-coffee px-3 py-2 rounded-lg hover:bg-white transition-colors shadow-sm text-sm font-medium"
              >
                + List Your Condo
              </button>
            </div>
          )}
        </div>

        {/* Location Filter */}
        {showFilters && (
          <div className="mb-8">
            <DiscoverFilters
              locationOnly
              currentFilters={locationFilters}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearFilters}
            />
          </div>
        )}

        {/* Content */}
        {loading && (
          <div className="text-center py-12 text-coffee">Loading condos...</div>
        )}

        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6 text-red-800">
            {error}
          </div>
        )}

        {!loading && !error && condos.length === 0 && (
          <div className="text-center py-12">
            {locationFilters.locations && locationFilters.locations.length > 0 ? (
              <>
                <p className="text-coffee text-xl mb-2">No condos match your filter</p>
                <p className="text-coffee opacity-80 mb-4">
                  Try a different borough or neighbourhood.
                </p>
                <button
                  onClick={handleClearFilters}
                  className="bg-terracotta text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm"
                >
                  Clear filters to see all listings
                </button>
              </>
            ) : (
              <>
                <p className="text-coffee text-xl mb-2">No condos yet</p>
                <p className="text-coffee opacity-80 mb-4">
                  Check back later for condo listings.
                </p>
                <button
                  onClick={handleBackToDashboard}
                  className="bg-terracotta text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm"
                >
                  Back to Dashboard
                </button>
              </>
            )}
          </div>
        )}

        {!loading && !error && condos.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {condos.map((condo) => (
                <CondoCard
                  key={condo.id ?? condo._id ?? condo.name}
                  condo={condo}
                  onCheckWaitlist={(id) => handleCheckWaitlist(id)}
                  isAuthenticated={isAuthenticated}
                />
              ))}
            </div>

            <WaitlistModal
              condoId={waitlistModal.condo?.id ?? waitlistModal.condo?._id ?? ''}
              condo={waitlistModal.condo}
              condoName={waitlistModal.condo?.name ?? ''}
              isOpen={waitlistModal.isOpen}
              onClose={handleWaitlistClose}
              onFetchWaitlistUsers={getWaitlistUsers}
              onJoinWaitlist={async (id) => {
                const res = await joinWaitlist(id);
                if (res.success) setCondoIsOnWaitlist(id, true);
                return res;
              }}
              onLeaveWaitlist={async (id) => {
                const res = await leaveWaitlist(id);
                if (res.success) setCondoIsOnWaitlist(id, false);
                return res;
              }}
              onRefresh={() => fetchCondos(pagination.page, pagination.limit, {
                locations: locationFilters.locations,
                neighborhoodIds: locationFilters.neighborhoodIds,
              })}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center gap-2">
                <button
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-4 py-2 rounded-lg border-2 border-coffee text-coffee disabled:opacity-50 disabled:cursor-not-allowed hover:bg-cream"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-coffee">
                  Page {pagination.page} of {totalPages}
                </span>
                <button
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page >= totalPages}
                  className="px-4 py-2 rounded-lg border-2 border-coffee text-coffee disabled:opacity-50 disabled:cursor-not-allowed hover:bg-cream"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CondosPage;
